import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const Security = () => {
  const { user, updateUser } = useAuth();
  const { success, error } = useNotification();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [securityLog, setSecurityLog] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [resetTokens, setResetTokens] = useState([]);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      setLoading(true);
      const [securityResponse, sessionsResponse, logResponse] = await Promise.all([
        axios.get('/api/users/security'),
        axios.get('/api/users/sessions'),
        axios.get('/api/users/security-log')
      ]);

      setIsTwoFactorEnabled(securityResponse.data.twoFactorEnabled || false);
      setIsGoogleConnected(securityResponse.data.googleConnected || false);
      setSessions(sessionsResponse.data.sessions || []);
      setSecurityLog(logResponse.data.logs || []);
      setResetTokens(securityResponse.data.recoveryTokens || []);
    } catch (err) {
      console.error('Failed to fetch security data:', err);
      error('Failed to load security information');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      error('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await axios.put('/api/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      success('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const setupTwoFactor = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/users/setup-2fa');
      setQrCodeUrl(response.data.qrCodeUrl);
      setShowQRModal(true);
    } catch (err) {
      error(err.response?.data?.message || 'Failed to setup two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async () => {
    try {
      setLoading(true);
      await axios.post('/api/users/verify-2fa', { code: twoFactorCode });
      setIsTwoFactorEnabled(true);
      setShowQRModal(false);
      setTwoFactorCode('');
      success('Two-factor authentication enabled successfully');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to verify two-factor code');
    } finally {
      setLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    try {
      setLoading(true);
      await axios.delete('/api/users/disable-2fa');
      setIsTwoFactorEnabled(false);
      success('Two-factor authentication disabled');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to disable two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const terminateSession = async (sessionId) => {
    try {
      await axios.delete(`/api/users/sessions/${sessionId}`);
      setSessions(sessions.filter(session => session.id !== sessionId));
      success('Session terminated successfully');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to terminate session');
    }
  };

  const generateRecoveryTokens = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/users/recovery-tokens');
      setResetTokens(response.data.tokens);
      success('Recovery tokens generated successfully. Please save these tokens safely!');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to generate recovery tokens');
    } finally {
      setLoading(false);
    }
  };

  const disconnectGoogle = async () => {
    try {
      await axios.delete('/api/users/disconnect-google');
      setIsGoogleConnected(false);
      success('Google account disconnected successfully');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to disconnect Google account');
    }
  };

  if (loading && !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">Account Security</h1>
        <p className="text-gray-400">
          Manage your account security settings, including authentication methods and sessions.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Password Management */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Password Management</h2>
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
                minLength={6}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
                minLength={6}
              />
            </div>
            
            <div className="mt-2 text-xs text-gray-500">
              Password must be at least 6 characters long and include at least one uppercase letter, one lowercase letter, and one number.
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="btn-neon w-full"
            >
              {loading ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Two-Factor Authentication */}
        <div className="card-astral p-6">
          <h2 className="text-xl font-bold text-white mb-4">Two-Factor Authentication</h2>
          
          <div className="bg-dark-200 p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold">2FA Status</h3>
                <p className="text-sm text-gray-400">
                  {isTwoFactorEnabled ? 
                    'Two-factor authentication is enabled for your account' : 
                    'Protect your account with two-factor authentication'}
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm ${
                isTwoFactorEnabled ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'
              }`}>
                {isTwoFactorEnabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>
          
          {isTwoFactorEnabled ? (
            <button 
              onClick={disableTwoFactor}
              className="btn-neon-red w-full"
              disabled={loading}
            >
              Disable Two-Factor Authentication
            </button>
          ) : (
            <button 
              onClick={setupTwoFactor}
              className="btn-neon w-full"
              disabled={loading}
            >
              Enable Two-Factor Authentication
            </button>
          )}
          
          <div className="mt-4 p-3 bg-blue-600/20 border border-blue-500/50 rounded-lg">
            <p className="text-blue-400 text-sm">
              Two-factor authentication adds an additional layer of security to your account by requiring a code from your mobile device in addition to your password.
            </p>
          </div>
        </div>
      </div>

      {/* Connected Accounts */}
      <div className="card-astral p-6">
        <h2 className="text-xl font-bold text-white mb-4">Connected Accounts</h2>
        
        <div className="bg-dark-200 p-4 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Google Account</h3>
                <p className="text-sm text-gray-400">
                  {isGoogleConnected ? 
                    'Your account is connected to Google' : 
                    'Connect your account with Google for easier sign-in'}
                </p>
              </div>
            </div>
            <div>
              {isGoogleConnected ? (
                <button 
                  onClick={disconnectGoogle}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                >
                  Disconnect
                </button>
              ) : (
                <button 
                  onClick={() => window.location.href = '/api/auth/google'}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-purple-600/20 border border-purple-500/50 rounded-lg">
          <p className="text-purple-400 text-sm">
            Connecting your account with third-party services allows for easier sign-in and additional security options.
          </p>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="card-astral p-6">
        <h2 className="text-xl font-bold text-white mb-4">Active Sessions</h2>
        
        {sessions.length === 0 ? (
          <p className="text-gray-400">No active sessions found.</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div key={session.id} className="bg-dark-200 p-4 rounded-lg flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-semibold">{session.device}</span>
                    {session.current && (
                      <span className="px-2 py-1 bg-green-600/20 text-green-400 text-xs rounded-full">
                        Current Session
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    <span>{session.browser}</span>
                    <span className="mx-2">•</span>
                    <span>{session.location || 'Unknown location'}</span>
                    <span className="mx-2">•</span>
                    <span>Last active: {new Date(session.lastActive).toLocaleString()}</span>
                  </div>
                </div>
                {!session.current && (
                  <button
                    onClick={() => terminateSession(session.id)}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors"
                  >
                    Terminate
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-yellow-600/20 border border-yellow-500/50 rounded-lg">
          <p className="text-yellow-400 text-sm">
            These are your currently active sessions. If you notice any suspicious activity, terminate the session and change your password immediately.
          </p>
        </div>
      </div>

      {/* Security Log */}
      <div className="card-astral p-6">
        <h2 className="text-xl font-bold text-white mb-4">Security Log</h2>
        
        {securityLog.length === 0 ? (
          <p className="text-gray-400">No security events recorded.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="text-left text-gray-400 border-b border-gray-700">
                <tr>
                  <th className="pb-2">Event</th>
                  <th className="pb-2">IP Address</th>
                  <th className="pb-2">Date & Time</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {securityLog.map((log, index) => (
                  <tr key={index} className="border-b border-gray-800">
                    <td className="py-3 text-white">{log.event}</td>
                    <td className="py-3 text-gray-400">{log.ipAddress}</td>
                    <td className="py-3 text-gray-400">{new Date(log.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Account Recovery */}
      <div className="card-astral p-6">
        <h2 className="text-xl font-bold text-white mb-4">Account Recovery</h2>
        
        <div className="bg-dark-200 p-4 rounded-lg mb-4">
          <h3 className="text-white font-semibold mb-2">Recovery Tokens</h3>
          <p className="text-sm text-gray-400 mb-3">
            Recovery tokens can be used to regain access to your account if you lose your two-factor authentication device.
          </p>
          
          {resetTokens.length > 0 ? (
            <div className="space-y-2">
              <p className="text-yellow-400 text-sm">Save these tokens in a secure location. They will not be shown again!</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {resetTokens.map((token, index) => (
                  <div key={index} className="bg-dark-100 p-2 rounded border border-gray-700 font-mono text-xs text-purple-400">
                    {token}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={generateRecoveryTokens}
              className="btn-neon-blue w-full"
              disabled={loading}
            >
              Generate Recovery Tokens
            </button>
          )}
        </div>
        
        <div className="mt-4 p-3 bg-red-600/20 border border-red-500/50 rounded-lg">
          <p className="text-red-400 text-sm">
            Warning: If you lose access to your two-factor authentication device and don't have recovery tokens, you may be permanently locked out of your account.
          </p>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Setup Two-Factor Authentication</h2>
            
            <div className="flex flex-col items-center mb-4">
              <p className="text-gray-400 text-sm mb-4 text-center">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <div className="bg-white p-2 rounded mb-4">
                <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Enter Verification Code
              </label>
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400 text-center text-xl tracking-widest"
                maxLength={6}
                placeholder="000000"
                required
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowQRModal(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={verifyTwoFactor}
                disabled={twoFactorCode.length !== 6}
                className={`flex-1 py-2 px-4 rounded transition-colors ${
                  twoFactorCode.length === 6
                    ? 'btn-neon'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                Verify & Enable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Security;
